"use client"

import { useState, useEffect } from "react"
import { Megaphone, SealPercent, Crown, X, Sparkles, Gem, Loader2 } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { useFeatureFlag } from "@/lib/feature-flags"
import { useAdStore } from "@/lib/ad-store"
import { toast } from "sonner"
import { useModalContent } from "@/lib/modal-content"
import { useRouter, useSearchParams } from "next/navigation"
import { usePatreonMode } from "@/lib/use-patreon-mode"
import { PATREON_SUBSCRIBE_PATH } from "@/lib/patreon/paths"
import { useAuthStore } from "@/lib/auth-store"
import { fetchSeenState, markSeen } from "@/lib/client/seen-state"
import { useModalSlot } from "@/lib/client/modal-coordinator"
import { FIRST_SEEN_KEY, LOCAL_FIRST_SEEN_DATE_KEY } from "./early-access-modal"
import { useTranslations } from "next-intl"

// The "FOUNDERS" title + slime-pool art is baked into the hero graphic (default
// /screens/founder/glym-founder.webp, replaceable in admin); the dark lower portion of
// the image is reserved for the overlay below (slot progress, perks, CTA) —
// same pattern as EarlyAccessModal.

// Account-scoped (see lib/client/seen-state.ts) — cooldown is computed from
// seen_at on the server so it survives a switch to a different browser/device,
// instead of resetting to "never dismissed" the moment localStorage is empty.
const DISMISSED_KEY = "modal:founder_promo"
const LOCAL_DISMISSED_KEY = "founder_modal_dismissed_at"
const COOLDOWN_DAYS = 7
const DELAY_MS = 5000 // show after 5s

interface FounderPromoModalProps {
  founderSlotsRemaining: number
  founderSlotsClaimed: number
  /** Admin preview: show it now, skipping every gate and never recording a dismissal. */
  forceOpen?: boolean
  onClose?: () => void
}

const DEFAULT_PERKS = [
  { icon: Megaphone, text: "No ads, forever" },
  { icon: Gem, text: "All Goon Max perks, forever" },
  { icon: SealPercent, text: "40% off credit packs, forever" },
  { icon: Crown, text: "Permanent Founder badge, forever" },
  { icon: Sparkles, text: "Your support keeps Goonru independent" },
  { icon: Sparkles, text: "One payment — no recurring charge, ever" },
]

// Patreon mode (docs/PATREON_PLAN_C.md): Founder is a $50 monthly Patreon tier, so nothing may say "forever" or "one payment".
// Only perks that work today; the admin-editable copy above is ignored in this mode.
const PATREON_PERKS = [
  { icon: Gem, text: "All Goon Max perks: no ads, 400k stamina/day, 12 min/day voice" },
  { icon: Crown, text: "A numbered Founder spot (our goal is 250 Founders)" },
  { icon: Crown, text: "Founder badge and frames; stay 2 months for Founder+" },
  { icon: Sparkles, text: "Your price stays locked while you stay" },
  { icon: Sparkles, text: "Your support keeps Goonru independent" },
]

export function FounderPromoModal({ founderSlotsRemaining, founderSlotsClaimed, forceOpen = false, onClose }: FounderPromoModalProps) {
  const t = useTranslations("founderPromoModal")
  const [visible, setVisible] = useState(false)
  // Art and copy the admin can edit (lib/modal-content-defs.ts). Only fetched
  // once the modal is about to show.
  const content = useModalContent("founder_promo", visible)
  const [subscribing, setSubscribing] = useState(false)
  const [heroLoaded, setHeroLoaded] = useState(true)
  const { enabled: modalEnabled, loading } = useFeatureFlag("founder_modal_enabled")
  const { isPremiumUser } = useAdStore()
  const { user, isLoading: authLoading } = useAuthStore()
  const searchParams = useSearchParams()
  const router = useRouter()
  const { enabled: patreonMode } = usePatreonMode()
  // Dev-only bypass: visit ?preview=founder locally to see this instantly,
  // skipping the flag, delay, day-gate, and cooldown gate.
  const forcePreview = process.env.NODE_ENV !== "production" && searchParams.get("preview") === "founder"

  const soldOut = founderSlotsRemaining <= 0

  useEffect(() => {
    if (forcePreview || forceOpen) { setVisible(true); return }
    if (loading || !modalEnabled || isPremiumUser || soldOut || authLoading) return

    let cancelled = false
    const run = async () => {
      if (user) {
        const { seenAt } = await fetchSeenState()
        if (cancelled) return
        const dismissedAt = seenAt[DISMISSED_KEY]
        if (dismissedAt) {
          const daysSince = (Date.now() - new Date(dismissedAt).getTime()) / 86_400_000
          if (daysSince < COOLDOWN_DAYS) return
        }
        // Don't stack on top of the early-access welcome modal — wait until the
        // user's second calendar day before pitching Founder.
        const firstSeen = seenAt[FIRST_SEEN_KEY]
        if (firstSeen && new Date(firstSeen).toISOString().slice(0, 10) === new Date().toISOString().slice(0, 10)) return
      } else {
        try {
          const dismissedAt = localStorage.getItem(LOCAL_DISMISSED_KEY)
          if (dismissedAt) {
            const daysSince = (Date.now() - Number(dismissedAt)) / 86_400_000
            if (daysSince < COOLDOWN_DAYS) return
          }
          const firstSeen = localStorage.getItem(LOCAL_FIRST_SEEN_DATE_KEY)
          const today = new Date().toISOString().slice(0, 10)
          if (firstSeen === today) return
        } catch {}
      }
      if (cancelled) return
      setTimeout(() => { if (!cancelled) setVisible(true) }, DELAY_MS)
    }
    void run()
    return () => { cancelled = true }
  }, [loading, modalEnabled, isPremiumUser, soldOut, authLoading, user, forcePreview, forceOpen])

  const dismiss = () => {
    if (forceOpen) { onClose?.(); return }
    setVisible(false)
    if (user) {
      void markSeen(DISMISSED_KEY)
    } else {
      try { localStorage.setItem(LOCAL_DISMISSED_KEY, String(Date.now())) } catch {}
    }
  }

  const handleSubscribe = async () => {
    // Patreon mode: Founder is bought on Patreon; the subscribe page explains it and sends people there.
    if (patreonMode) { dismiss(); router.push(PATREON_SUBSCRIBE_PATH); return }
    setSubscribing(true)
    try {
      const res = await fetch("/api/subscriptions/founder-checkout", { method: "POST" })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || "Failed to start checkout")
      window.location.href = data.url
    } catch (err: any) {
      toast.error(err.message || t("somethingWentWrong"))
      setSubscribing(false)
    }
  }

  // Wait for the admin-editable art/copy so the built-in image never flashes
  // up and then gets swapped for an override.
  const canShow = useModalSlot("founderPromo", visible && heroLoaded && content.ready, { bypass: forcePreview || forceOpen })

  if (!visible || !heroLoaded || !content.ready || !canShow) return null

  const fillPct = Math.min(100, (founderSlotsClaimed / 250) * 100)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-sm bg-background rounded-lg shadow-2xl overflow-hidden">
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-black/45 backdrop-blur-md border border-white/10 flex items-center justify-center text-white/90 opacity-50 hover:opacity-100 hover:bg-black/65 hover:text-white active:opacity-100 focus-visible:opacity-100 transition-opacity"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative">
          {/* Plain <img>: an uploaded override lives on the storage host, which
              next/image would need whitelisting for. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={content.art("hero")}
            alt={t("goonruFounders")}
            className="w-full h-auto block"
            onError={() => setHeroLoaded(false)}
          />

          {/* Overlay sits inside the art's own dark lower zone. Previously
              lifted to -translate-y-[50px] to fit a 5th perk line without
              crowding the CTA — reported live as now sitting too high,
              leaving dead space below the CTA. Eased back down to
              -translate-y-[20px], still enough room for all 5 perk lines. */}
          <div className="absolute inset-x-0 bottom-0 top-[50%] px-9 pt-3 pb-6 flex flex-col justify-center gap-4 -translate-y-[20px]">
            {/* Slot progress */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-white/70">{content.text("claimed_label", t("founderSlotsClaimed250Claimed", { founderSlotsClaimed }), { claimed: founderSlotsClaimed })}</span>
                <span className="text-amber-300 font-semibold">{content.text("remaining_label", t("founderSlotsRemainingLeft", { founderSlotsRemaining }), { remaining: founderSlotsRemaining })}</span>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-amber-500 to-purple-500 transition-all"
                  style={{ width: `${fillPct}%` }}
                />
              </div>
            </div>

            {/* Perks */}
            <ul className="space-y-2">
              {(patreonMode ? PATREON_PERKS : DEFAULT_PERKS).map(({ icon: Icon, text: defaultText }, i) => {
                const text = patreonMode ? defaultText : content.text(`perk_${i + 1}`, defaultText)
                return (
                <li key={i} className="flex items-start gap-3">
                  <Icon className="h-5 w-5 mt-0.5 text-amber-300 flex-shrink-0" />
                  <span className="text-sm text-white/80 leading-snug">{text}</span>
                </li>
                )
              })}
            </ul>

            {/* CTA */}
            <div className="space-y-2.5 pt-1">
              <Button
                onClick={handleSubscribe}
                disabled={subscribing}
                className="w-full h-12 rounded-full text-base bg-gradient-to-r from-violet-600 to-purple-500 hover:from-violet-500 hover:to-purple-400 text-white border-0 shadow-[0_0_16px_rgba(139,92,246,0.4)]"
              >
                {subscribing ? (
                  <><Loader2 className="h-5 w-5 mr-2 animate-spin" />{content.text("cta_loading", t("redirecting"))}</>
                ) : (
                  <><Crown className="h-5 w-5 mr-2" />{patreonMode ? "Become a Founder, $50/mo" : content.text("cta", t("becomeAMember79"))}</>
                )}
              </Button>

              <button onClick={dismiss} className="w-full rounded-full px-5 py-2.5 text-sm font-medium text-white/70 bg-white/5 hover:bg-white/10 hover:text-white/95 border border-white/10 transition-colors">
                {content.text("dismiss", t("noThanks"))}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
